import { useEffect, useRef } from "react";

/**
 * useScrollReveal
 * Adds the class "is-visible" to the element when it enters the viewport.
 * Uses IntersectionObserver, fires once.
 *
 * Usage:
 *   const ref = useScrollReveal();
 *   <div ref={ref} className="reveal-on-scroll">...</div>
 */
export default function useScrollReveal({
  threshold = 0.15,
  rootMargin = "0px 0px -80px 0px",
} = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      el.classList.add("is-visible");
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  return ref;
}

/**
 * useStaggerReveal
 * Reveals direct children one by one when the parent enters the viewport.
 */
export function useStaggerReveal({
  threshold = 0.12,
  rootMargin = "0px 0px -60px 0px",
  delayStep = 80,
} = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const items = Array.from(el.querySelectorAll("[data-stagger-item]"));
    items.forEach((it, i) => {
      it.style.transitionDelay = `${i * delayStep}ms`;
    });

    if (typeof IntersectionObserver === "undefined") {
      el.classList.add("is-visible");
      items.forEach((it) => it.classList.add("is-visible"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            items.forEach((it) => it.classList.add("is-visible"));
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin, delayStep]);

  return ref;
}
